import {
  Badge,
  Box,
  Button,
  IconButton,
  Popover,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { ECTopbar } from './ECTopbar';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import FolderIcon from '@mui/icons-material/Folder';
import SettingsIcon from '@mui/icons-material/Settings';
import { ExoSkeleton } from '../exo/ExoSkeleton';
import { useState } from 'react';
import ExoDialog from '../exo/ExoDialog';
import { ECSettings } from './ECSettings';
import uuid from 'react-uuid';
import WarningIcon from '@mui/icons-material/Warning';
import moment from 'moment';
import { API_GET, DBRequest, useConfig } from '../../api/api';
import { useDroppable } from '@dnd-kit/core';
import Cookies from 'universal-cookie';
import { NewDefaultEmailIntegration } from '../new/NewDefaultEmailIntegration';

export const folderOrder = [
  'INBOX',
  'Drafts',
  'Sent Items',
  'Spam',
  'Trash',
  'Archive',
];

export const ECInboxList = ({
  onOpen,
  inboxes,
  onSelect,
  onInboxChange,
  selectedFolder,
  folderMap,
  width,
  isLoading,
  onOpenSettings,
  hasDnd,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  // smartfolder

  const smartFolders = [
    {
      id: 'S1',
      name: 'INBOX',
      type: 'INBOX',
      isOutgoing: false,
    },
    {
      id: 'S2',
      name: 'Sent Items',
      type: 'Sent Items',
      isOutgoing: true,
    },
  ];

  function handleSmartFolderSelect(folder) {
    onSelect(folder);
  }

  return (
    <Box
      sx={{
        width,
      }}
      className="flex flex-col gap-2 relative justify-between h-full"
    >
      <Box className="flex flex-col gap-2 h-full overflow-y-auto w-full">
        <ECTopbar
          label={t('Inboxes')}
          ActionIcon={AddIcon}
          actionTooltip={t('Add E-Mail Account')}
          onAction={() => onOpen(true)}
        />
        {isLoading ? (
          <ExoSkeleton n={8} />
        ) : inboxes ? (
          <>
            <Box className="flex flex-col px-2 pb-4">
              <Typography
                fontSize="12px"
                fontWeight="600"
                className="opacity-50"
              >
                {t('All accounts')}
              </Typography>
              <Box className="flex flex-col">
                {smartFolders.map((folder) => (
                  <Folder
                    folder={folder}
                    onSelect={handleSmartFolderSelect}
                    selectedFolder={selectedFolder}
                    folderMap={folderMap}
                  />
                ))}
              </Box>
            </Box>
            {inboxes.map((inbox) => (
              <Inbox
                key={inbox.id}
                inbox={inbox}
                onSelect={onSelect}
                selectedFolder={selectedFolder}
                folderMap={folderMap}
                hasDnd={hasDnd}
                onInboxChange={onInboxChange}
              />
            ))}
          </>
        ) : (
          <>Add Account</>
        )}
      </Box>
      <Box>
        <IconButton size="small" onClick={onOpenSettings}>
          <SettingsIcon />
        </IconButton>
      </Box>
    </Box>
  );
};

export const Inbox = ({
  inbox,
  onSelect,
  selectedFolder,
  folderMap,
  fromMove,
  hasDnd,
  onInboxChange,
  onDelete,
}) => {
  const theme = useTheme();
  const config = useConfig();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { label, color, imapUsername, imapStatus, smtpStatus } = inbox;
  const { folders } = inbox.inbox;

  const cookies = new Cookies();
  const userId = cookies.get('userId');

  // re-auth popper
  const [reAuthenticateAnchorEl, setReAuthenticateAnchorEl] = useState(null);

  //To open new email integration dialog
  const [openNewEmailIntegration, setOpenNewEmailIntegration] = useState(false);
  const [selectedInbox, setSelectedInbox] = useState(null);

  const sortedFolders = folders.slice().sort((a, b) => {
    const indexA = folderOrder.indexOf(a.name);
    const indexB = folderOrder.indexOf(b.name);

    const inOrderA = indexA !== -1;
    const inOrderB = indexB !== -1;

    if (inOrderA && inOrderB) {
      return indexA - indexB; // both in folderOrder -> sort by their order
    }
    if (inOrderA) return -1; // only A is in folderOrder -> A comes first
    if (inOrderB) return 1; // only B is in folderOrder -> B comes first

    return a.name.localeCompare(b.name); // neither in folderOrder -> sort alphabetically
  });

  // called from popper to re-authenticate
  function handleReAuthenticateInbox(inboxData) {
    DBRequest({
      config,
      path: `email-credentials/${inboxData.id}/re-authenticate`,
      method: API_GET,
      onResponse: (res) => {
        const url = res.data?.authUrl;
        if (url) {
          window.location.assign(url);
        } else {
          setOpenNewEmailIntegration(true);
        }
      },
    });
  }

  return (
    <Box
      className="px-2 pb-4"
      sx={{
        '.dnd-heighlight .folder-button': {
          bgcolor: colors.info + '30 !important',
          border: `1px solid ${colors.info}80 !important`,
        },
      }}
    >
      <Typography
        fontSize={11}
        fontWeight={600}
        className="flex flex-col items-start gap-0"
      >
        {/* label and icon row */}
        <Box className="flex items-center gap-1">
          <span className="opacity-50">{label}</span>
          {(imapStatus !== 1 || smtpStatus !== 1) && (
            <Tooltip title={t(`Reauthentication required`)}>
              <WarningIcon
                color="warning"
                sx={{
                  fontSize: 16,
                  cursor: 'pointer',
                }}
                onClick={(e) => {
                  setReAuthenticateAnchorEl(e.target);
                  setSelectedInbox(inbox); // to know which inbox to re-auth
                }}
              />
            </Tooltip>
          )}
        </Box>
        <span style={{ fontWeight: 400 }} className="opacity-50 pl-2 italic">
          {`<${imapUsername}>`}
        </span>
      </Typography>

      {sortedFolders.map((folder) => (
        <Folder
          key={folder.id}
          folder={folder}
          onSelect={onSelect}
          selectedFolder={selectedFolder}
          folderMap={folderMap}
          fromMove={fromMove}
          hasDnd={hasDnd}
        />
      ))}
      <Popover
        anchorEl={reAuthenticateAnchorEl}
        open={Boolean(reAuthenticateAnchorEl)}
        onClose={() => {
          setReAuthenticateAnchorEl(null);
          setSelectedInbox(null);
        }}
        disable
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box className=" flex flex-col justify-center items-center w-full h-full gap-4 p-2 md:max-w-[350px]">
          <Typography fontSize={15} className="text-center">
            {t('Your connection has expired. Please sign in again.')}
          </Typography>
          <Button
            onClick={() => handleReAuthenticateInbox(selectedInbox)}
            variant="contained"
            color="info"
          >
            {t('Authenticate')}
          </Button>
        </Box>
      </Popover>
      <ExoDialog
        open={openNewEmailIntegration}
        onClose={() => {
          setOpenNewEmailIntegration(false);
          setReAuthenticateAnchorEl(null);
          setSelectedInbox(null);
        }}
        limitWidth
      >
        <NewDefaultEmailIntegration
          onChange={onInboxChange}
          startData={selectedInbox}
          userid={userId}
          onDelete={onDelete}
          onClose={() => {
            setOpenNewEmailIntegration(false);
            setReAuthenticateAnchorEl(null);
            setSelectedInbox(null);
          }}
        />
      </ExoDialog>
    </Box>
  );
};

export const Folder = ({
  folder,
  onSelect,
  selectedFolder,
  folderMap,
  fromMove,
  hasDnd,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const config = useConfig();

  const { t } = useTranslation();

  const { name, id, unseenCount } = folder;

  const Icon = folderMap[name]?.Icon || FolderIcon;
  const label = folderMap[name]?.label || name;
  const color = folderMap[name]?.color || 'unset';

  const isSelected = selectedFolder?.id === id;

  const formatedUnseenCount = unseenCount > 99 ? '99+' : unseenCount;

  function getDateLabel(dateTime) {
    const dateMoment = moment(dateTime);
    const now = moment();

    if (dateMoment.isSame(now, 'day')) {
      return [dateMoment.format('HH:mm'), true];
    } else if (dateMoment.isSame(now.clone().subtract(1, 'day'), 'day')) {
      return [`${t('yesterday')} ${dateMoment.format('HH:mm')}`, false];
    } else {
      return [dateMoment.format(config?.general?.dateOutputFormat), false];
    }
  }
  const [dateLabel, isToday] = getDateLabel(folder.lastSuccessfulSyncAt);
  const tooltip =
    t(`This inbox is currently not synchronized.`) +
    (folder?.lastSuccessfulSyncAt
      ? ' ' +
        (isToday
          ? t(`Last successful sync was today at`)
          : t(`Last successful sync was at`)) +
        ' ' +
        `${dateLabel}`
      : '');

  const Container = hasDnd ? DroppableContainer : Box;

  return (
    <Container droppableId={`folder${folder.id}`} folder={folder}>
      <Button
        onClick={() => onSelect(folder)}
        sx={{
          padding: '3px 10px',
          justifyContent: 'start',
          borderRadius: '5px',
          bgcolor: isSelected && !fromMove ? colors.card : 'transparent',
          border: `1px solid transparent`,
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
          },
          '.icon': { fontSize: '16px' },
          '@media (max-width: 1024px)': {
            height: '60px',
            '.text': {
              fontSize: '18px',
            },
            '.icon': {
              fontSize: '20px',
            },
          },
        }}
        className="w-full flex gap-2 folder-button"
      >
        <Box className="flex w-full gap-2 items-center">
          <Icon sx={{ color }} className="opacity-50 icon" />
          <Typography className="text">{label}</Typography>
        </Box>
        {!folder?.syncStatus?.errorEncountered && unseenCount > 0 && (
          <Typography
            fontSize={10}
            fontWeight={500}
            sx={{
              bgcolor: colors.info + '30',
              border: `1px solid ${colors.info}80`,
            }}
            className="px-1 rounded-full"
          >
            {formatedUnseenCount}
          </Typography>
        )}
        {(folder?.syncStatus?.errorEncountered ||
          moment(folder?.lastSuccessfulSyncAt).isBefore(
            moment().subtract(1, 'hour')
          )) && (
          <Tooltip title={tooltip}>
            <WarningIcon color="warning" />
          </Tooltip>
        )}
      </Button>
    </Container>
  );
};

const DroppableContainer = ({ droppableId, children, folder }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: folder,
  });
  return (
    <Box
      ref={setNodeRef}
      key={droppableId}
      className={isOver ? 'dnd-heighlight' : ''}
    >
      {children}
    </Box>
  );
};
